import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { Subject, Subscription, Observable } from 'rxjs';
import { DwspayService } from '../../services/dwspay.service';

@Component({
  selector: 'app-dwspay',
  standalone: true,
  imports: [],
  templateUrl: './dwspay.component.html',
  styleUrls: ['./dwspay.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DwspayComponent implements OnInit, OnDestroy, OnChanges {
  @Input() clientSecret?: string;
  
  private subscriptions: Subscription[] = [];
  private paymentElementReadySubject: Subject<boolean> = new Subject<boolean>();
  private paymentElementChangeSubject: Subject<any> = new Subject<any>();
  private isInitialized: boolean = false;
  public isPaymentElementValid: boolean = false;

  constructor(
    public dwspayService: DwspayService
  ) {
  }

  ngOnInit() {
    // Check if clientSecret is already available when component initializes
    if (this.clientSecret && !this.isInitialized) {
      console.debug('DWSPay Component: clientSecret available in ngOnInit');
      this.initializePaymentElement();
    } else {
      console.debug('DWSPay Component: ngOnInit - clientSecret:', this.clientSecret ? 'available' : 'not available');
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // Handle both initial value and subsequent changes
    if (changes['clientSecret']) {
      const currentValue = changes['clientSecret'].currentValue;
      const previousValue = changes['clientSecret'].previousValue;
      
      console.debug('DWSPay Component: ngOnChanges - clientSecret changed', {
        currentValue: currentValue ? 'available' : 'null',
        previousValue: previousValue ? 'available' : 'null',
        isInitialized: this.isInitialized
      });
      
      // Initialize if we have a new client secret and haven't initialized yet
      if (currentValue && currentValue !== previousValue && !this.isInitialized) {
        console.debug('DWSPay Component: Initializing Payment Element from ngOnChanges');
        this.initializePaymentElement();
      }
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.dwspayService.destroyPaymentElement();
  }

  /**
   * Initialize Payment Element when client secret is available
   */
  private async initializePaymentElement(): Promise<void> {
    if (!this.clientSecret || this.isInitialized) {
      return;
    }

    try {
      // Wait a bit to ensure the DOM element is available
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Check if the container element exists
      const container = document.getElementById('dwspay-payment-element');
      if (!container) {
        console.warn('DWSPay: Container element not found, retrying...');
        // Retry after a short delay
        setTimeout(() => {
          if (!this.isInitialized && this.clientSecret) {
            this.initializePaymentElement();
          }
        }, 500);
        return;
      }

      await this.dwspayService.initPaymentElement(this.clientSecret, 'dwspay-payment-element');
      this.isInitialized = true;
      
      // Subscribe to ready event
      const readySub = this.dwspayService.onPaymentElementReady().subscribe((ready) => {
        this.paymentElementReadySubject.next(ready);
        this.isPaymentElementValid = false; // Initially not valid
      });
      this.subscriptions.push(readySub);

      // Subscribe to change events (validation)
      const changeSub = this.dwspayService.onPaymentElementChange().subscribe((event) => {
        this.paymentElementChangeSubject.next(event);
        this.isPaymentElementValid = event.complete && !event.error;
      });
      this.subscriptions.push(changeSub);
    } catch (error) {
      console.error('Error initializing Payment Element:', error);
      // Reset initialization flag to allow retry
      this.isInitialized = false;
    }
  }

  /**
   * Observable for Payment Element ready event
   */
  public onReady(): Observable<boolean> {
    return this.paymentElementReadySubject.asObservable();
  }

  /**
   * Observable for Payment Element change events
   */
  public onChange(): Observable<any> {
    return this.paymentElementChangeSubject.asObservable();
  }
}
